import { Link, NavLink } from 'react-router-dom';
import BrandLogo from '../BrandLogo';

const navItems = [
  { label: 'Home', path: '/', icon: 'bi-house-door' },
  { label: 'Dashboard', path: '/guest/dashboard', icon: 'bi-speedometer2' },
  { label: 'My Bookings', path: '/guest/bookings', icon: 'bi-journal-text', badge: 'totalBookings' },
  { label: 'Upcoming Stays', path: '/guest/upcoming', icon: 'bi-calendar2-week', badge: 'upcomingStays' },
  { label: 'Payments', path: '/guest/payments', icon: 'bi-credit-card', badge: 'pendingPayments' },
  { label: 'Invoices', path: '/guest/invoices', icon: 'bi-receipt' },
  { label: 'Refunds', path: '/guest/refunds', icon: 'bi-arrow-counterclockwise' },
  { label: 'Messages', path: '/guest/messages', icon: 'bi-chat-dots', badge: 'unreadMessages' },
  { label: 'Wishlist', path: '/guest/wishlist', icon: 'bi-heart' },
  { label: 'Support', path: '/guest/support', icon: 'bi-life-preserver', badge: 'openTickets' },
  { label: 'Profile Settings', path: '/guest/profile', icon: 'bi-gear' },
];

const resolveAvatar = (profile, apiBase) => {
  if (!profile?.avatarUrl) return '';
  if (profile.avatarUrl.startsWith('http')) return profile.avatarUrl;
  return `${apiBase}${profile.avatarUrl.startsWith('/') ? profile.avatarUrl : `/${profile.avatarUrl}`}`;
};

const Badge = ({ value, collapsed }) => {
  if (!value || Number(value) <= 0) return null;
  if (collapsed) {
    return <span className="ml-auto h-2 w-2 rounded-full bg-emerald-400" />;
  }
  return (
    <span className="ml-auto rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700">
      {value}
    </span>
  );
};

const GuestSidebar = ({
  profile,
  summary,
  collapsed,
  onToggleCollapse,
  mobileOpen,
  onCloseMobile,
  onLogout,
}) => {
  const apiBase = process.env.REACT_APP_API_BASE_URL || '';
  const avatar = resolveAvatar(profile, apiBase);
  const initials = profile?.name?.slice(0, 2)?.toUpperCase() || 'HV';
  const shellWidth = collapsed ? 'w-20' : 'w-72';
  const translate = mobileOpen ? 'translate-x-0' : '-translate-x-full';

  return (
    <>
      <div
        className={`fixed inset-0 z-40 bg-slate-950/60 transition-opacity lg:hidden ${
          mobileOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
        onClick={onCloseMobile}
        role="button"
        tabIndex={-1}
        aria-label="Close sidebar"
      />
      <aside
        className={`fixed left-0 top-0 z-50 h-full ${shellWidth} ${translate} transition-all duration-200 lg:translate-x-0`}
      >
        <div className="flex h-full flex-col bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-slate-100 shadow-xl">
          <div className="flex items-center justify-between px-4 py-5">
            <div className={`flex items-center gap-3 ${collapsed ? 'justify-center w-full' : ''}`}>
              <Link to="/" title="Home">
                <BrandLogo
                  size={40}
                  className="rounded-lg bg-white"
                  imgClassName="p-1"
                  fallback="HV"
                  fallbackClassName="text-slate-900 font-semibold"
                />
              </Link>
              {!collapsed && (
                <div>
                  <p className="text-xs uppercase tracking-widest text-emerald-200">Homevilas</p>
                  <p className="text-sm font-semibold text-white">Guest Suite</p>
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={onToggleCollapse}
              className="hidden h-8 w-8 items-center justify-center rounded-lg bg-white/10 text-sm text-white hover:bg-white/20 lg:flex"
              title={collapsed ? 'Expand' : 'Collapse'}
            >
              <i className={`bi ${collapsed ? 'bi-chevron-right' : 'bi-chevron-left'}`} />
            </button>
          </div>

          <div className={`px-4 ${collapsed ? 'hidden' : ''}`}>
            <div className="rounded-2xl bg-white/10 px-4 py-3">
              <div className="flex items-center gap-3">
                {avatar ? (
                  <img src={avatar} alt={profile?.name || 'Guest'} className="h-10 w-10 rounded-full object-cover" />
                ) : (
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-500 text-white font-semibold">
                    {initials}
                  </div>
                )}
                <div>
                  <p className="text-sm font-semibold text-white">{profile?.name || 'Guest'}</p>
                  <p className="text-xs text-slate-300">{profile?.email || 'guest@homevilas.com'}</p>
                </div>
              </div>
              <span className="mt-2 inline-flex rounded-full bg-emerald-500/20 px-2 py-0.5 text-xs text-emerald-200">
                Guest
              </span>
            </div>
          </div>

          <nav className="mt-6 flex-1 space-y-1 px-3">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                title={collapsed ? item.label : undefined}
                className={({ isActive }) =>
                  `group relative flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium transition ${
                    isActive
                      ? 'bg-emerald-500/15 text-white ring-1 ring-emerald-400/30'
                      : 'text-slate-200 hover:bg-white/10'
                  }`
                }
              >
                <i className={`bi ${item.icon} text-base`} />
                {!collapsed && <span>{item.label}</span>}
                <Badge value={summary?.[item.badge]} collapsed={collapsed} />
                {collapsed && (
                  <span className="pointer-events-none absolute left-full ml-3 rounded-lg bg-slate-900 px-2 py-1 text-xs text-white opacity-0 shadow-lg transition group-hover:opacity-100">
                    {item.label}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="px-3 pb-6">
            <button
              type="button"
              onClick={onLogout}
              className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-sm font-semibold text-slate-200 hover:bg-white/10"
              title={collapsed ? 'Logout' : undefined}
            >
              <i className="bi bi-box-arrow-right text-base" />
              {!collapsed && <span>Logout</span>}
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default GuestSidebar;

